import React, { useState } from "react";
import "./styles/widget-preview.scss";
import "./styles/markdown-light.css";
import { Icon, Spin, Tabs } from "antd";
import moment from "moment";
import { marked } from "marked"
import DOMPurify from 'dompurify';

import { IWidgetPreview } from './widgetPreview/constants'
import WidgetRating from "./WidgetRating";
import WidgetCarousel from "./WidgetCarousel";

import { WriteReview } from "./widgetPreview/review/WriteReview";
import { hasValue } from '../../../constant/utils';
import { GCPENDPOINT } from "../../../constant/common";
import WidgetInstall from "./WidgetInstall";
import { ReactComponent as LayerLeft } from "../../../assets/svg/layer-left.svg";
import { useHistory } from "react-router";
import ReviewModal from "./widgetPreview/review/ReviewModal";
import placeholderImg from "../../../assets/images/imagePlaceholder.png";

const { TabPane } = Tabs;

interface IWidgetDetails {
  getProductPreview: () => void;
  getProductReview: () => void;
  selectedOrg: string;
  productId: string;
  currentUser: string;
  preview: IWidgetPreview | null;
  avgRating: string;
  accessibility: boolean;
  version_tag: any;
  review:any;
  readme: { available: boolean, text: string }
}


export const WidgetPreview: React.FC<IWidgetDetails> = ({ 
  preview,
  getProductPreview,
  getProductReview,
  selectedOrg,
  productId,
  currentUser,
  avgRating,
  review,
  accessibility,
  readme,
}): JSX.Element => {
  const [copied, setCopied] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false)
  const [visibleReviews, setVisibleReviews] = useState<boolean>(false)
  const [tabView, setTabView] = useState("desc")
  const history: any = useHistory()
  const copy = (): void => {
    const el = document.createElement("input");
    el.value = window.location.href;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    setCopied(true);
  };

  const showModal = (): void => {
    setVisible(true)
  }
  const showModalReview = (): void => {
    setVisibleReviews(true)
  }

  const antIcon = <Icon type="loading" style={{ fontSize: 24 }} spin />;
  if (!preview) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin indicator={antIcon} style={{ color: '#15808C' }} />
      </div>)
  }

  const {
    id,
    name,
    preview_image,
    description,
    short_description,
    version_tag,
    created_by,
    is_paid,
    is_installed,
    author_website,
    reviews,
    released_at,
    screenshots,
    tags,
    release_notes,
  } = preview;
  const { first_name, last_name } = created_by;
  const [userRating]: any = reviews.filter((el: any) => (el.reviewed_by.id === currentUser))

  return (
    <div className="wrapper">
      {!accessibility &&
        <div className="widget-header">
          <div className="widget">
            <LayerLeft className="widget-icon" onClick={() => history.goBack()} fill={"black"} />
            <span data-test-id={`${name}`} className="widget-title">{name}</span>
          </div>
          <div className="widget-button">
            <button className={`widget-button__free ${is_paid ? "paid" : ""}`}>
              {is_paid ? "Paid" : "Free"}
            </button>
            <WidgetInstall widgetId={id} orgId={selectedOrg} is_installed={is_installed} name={name} />
          </div>
        </div>
      }
      <div className="widget-preview">
        <div className="preview-image">
          <img
            src={preview_image ? `${preview_image.startsWith("https://") ? preview_image : GCPENDPOINT+preview_image}` : placeholderImg}
            alt="thumbnail"
            className="main-image"
          />
          <div className="widget-details">
            <p className="widget-title">{name}</p>
            <p className="widget-subtitle">{short_description}</p>
          </div>
        </div>
        <div className="customer-rating-top">
          <WidgetRating avgRating={avgRating} />
          <p className="ratings">{reviews.length} ratings</p>
        </div>
      </div>

      <div className="widget-tab">
        <Tabs activeKey={tabView} onChange={(key) => setTabView(key)}>
          <TabPane tab="Description" key="desc">
            <p className="widget-description">{description}</p>
          </TabPane>
          <TabPane tab="README" key="readme">
            {readme.available ? (
              <div className="markdown-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(readme.text)) }}></div>
            ) : (
              <p className="widget-description">
                There is no README file associated with this plugin.
              </p>
            )}
          </TabPane>
        </Tabs>
      </div>

      {hasValue(screenshots) &&
        <div className="carousel">
          <WidgetCarousel screenshots={screenshots} />
        </div>
      }
      {hasValue(tags) &&
        <div className="graph-type">
          {tags.map((el: string, index: number) =>
            <li data-test-id={`${el}`} key={index}>{el}</li>
          )}
        </div>
      }
      <div className="whats-new">
        {hasValue(release_notes) &&
          <div className="update-description">
            <span className="update-title">What's New</span>
            <div>
              <p>{release_notes}</p>
            </div>
          </div>
        }
        <div className="release-date">
          <p className="update-title">Version:</p>
          <p>{version_tag}</p>
          <p>Release Date: {moment(released_at).format("MMM Do YYYY")}</p>
        </div>
      </div>
      <div className="rating">
        <span className="rating-title">Ratings</span>
        <div className="leave-rating">
          <div className="rating-preview">
            <div className="rating-header">Leave Ratings</div>
            <WidgetRating avgRating={userRating ? userRating.rating : 0} />
            {is_installed &&
              <div className="write-review" onClick={showModal}>{userRating ? "Edit Review" : "Write Review"}</div>
            }
            {visible &&
              <WriteReview visible={visible} setVisible={setVisible} selectedOrg={selectedOrg} productId={productId} getProductReview={getProductReview} getProductPreview={getProductPreview} reviews={review} currentUser={currentUser} is_installed={is_installed} />
            }
          </div>
          <div className="vl"></div>
          <div className="reviews">
            <span className="average-rating">{avgRating}</span>
            <span className="customer-rating">
              <WidgetRating avgRating={avgRating} height={50} />
              <div className="ratings" onClick={showModalReview}>{reviews.length} ratings</div>
              <ReviewModal
                avgRating={avgRating}
                reviews={reviews}
                version_tag={version_tag}
                visible={visibleReviews}
                setVisible={setVisibleReviews}
                productId={productId}
              />
            </span>
          </div>
        </div>
      </div>
      <div className="footer">
        <div className="about">
          <span className="author">
            <h4>Author</h4>
            <p>{first_name}{last_name}</p>
          </span>
          <span className="website">
            <h4>website</h4>
            <p>{author_website}</p>
          </span>
          <div className="report">
            <h4>Get Support</h4>
            <h4>Report</h4>
          </div>
        </div>
        <div className="share">
          <h4>share</h4>
          <p data-test-id="copy" className="copy-link" onClick={copy}>{!copied ? "Copy Link" : "Copied..!"}</p>
        </div>
      </div>
    </div>
  );
};
